import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { formatUSPhoneInput, toE164US } from '../utils/phone';
import { formatUtcNaiveInstantForViewer, getViewerTimezone } from '../utils/dateTime';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import './PaymentsPage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#1E293B',
      '::placeholder': { color: '#94A3B8' },
      iconColor: '#6B4EFF',
    },
    invalid: { color: '#EF4444', iconColor: '#EF4444' },
  },
};

// ── Inner Add Card Form (needs Stripe hooks) ──────────────────────────────────
const AddCardFormInner = ({ onSuccess, onCancel, getFirebaseToken, showToast }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [billingForm, setBillingForm] = useState({
    name: '', phone: '', address: '', city: '', state: '', postalCode: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete) return;

    setSaving(true);
    try {
      const normalizedPhone = billingForm.phone ? toE164US(billingForm.phone) : undefined;
      // Step 1: Create Stripe PaymentMethod (same as mobile createPaymentMethod)
      const cardElement = elements.getElement(CardElement);
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingForm.name || undefined,
          phone: normalizedPhone,
          address: {
            line1: billingForm.address || undefined,
            city: billingForm.city || undefined,
            state: billingForm.state || undefined,
            postal_code: billingForm.postalCode || undefined,
            country: 'US',
          },
        },
      });

      if (error) { showToast(error.message || 'Card error'); setSaving(false); return; }

      // Step 2: Send paymentMethodId to backend (same as mobile attachPaymentMethod)
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.paymentMethods,
        {
          paymentMethodId: paymentMethod.id,
          name: billingForm.name || undefined,
          phone: normalizedPhone,
          address: billingForm.address || undefined,
          city: billingForm.city || undefined,
          state: billingForm.state || undefined,
          postalCode: billingForm.postalCode || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('Card saved successfully');
      onSuccess();
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pay-add-card-form">
      <h3 className="pay-add-card-form-title">Add New Card</h3>
      <form onSubmit={handleSubmit} className="pay-card-form-grid">

        {/* Cardholder Name */}
        <div className="pay-form-group pay-form-full">
          <label className="pay-form-label">Cardholder Name</label>
          <input
            className="pay-form-input"
            type="text"
            placeholder="Jane Smith"
            value={billingForm.name}
            onChange={(e) => setBillingForm({ ...billingForm, name: e.target.value })}
            required
          />
        </div>

        {/* Stripe Card Element */}
        <div className="pay-form-group pay-form-full">
          <label className="pay-form-label">Card Details</label>
          <div className="pay-stripe-card-wrap">
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>
        </div>

        {/* Phone */}
        <div className="pay-form-group">
          <label className="pay-form-label">Phone (optional)</label>
          <input
            className="pay-form-input"
            type="tel"
            placeholder="(555) 123-4567"
            value={billingForm.phone}
            onChange={(e) => setBillingForm({ ...billingForm, phone: formatUSPhoneInput(e.target.value) })}
          />
        </div>

        {/* Address */}
        <div className="pay-form-group">
          <label className="pay-form-label">Address (optional)</label>
          <input
            className="pay-form-input"
            type="text"
            placeholder="123 Main St"
            value={billingForm.address}
            onChange={(e) => setBillingForm({ ...billingForm, address: e.target.value })}
          />
        </div>

        {/* City */}
        <div className="pay-form-group">
          <label className="pay-form-label">City (optional)</label>
          <input
            className="pay-form-input"
            type="text"
            placeholder="New York"
            value={billingForm.city}
            onChange={(e) => setBillingForm({ ...billingForm, city: e.target.value })}
          />
        </div>

        {/* State */}
        <div className="pay-form-group">
          <label className="pay-form-label">State (optional)</label>
          <input
            className="pay-form-input"
            type="text"
            placeholder="NY"
            maxLength={2}
            value={billingForm.state}
            onChange={(e) => setBillingForm({ ...billingForm, state: e.target.value.toUpperCase() })}
          />
        </div>

        {/* Postal Code */}
        <div className="pay-form-group">
          <label className="pay-form-label">ZIP Code (optional)</label>
          <input
            className="pay-form-input"
            type="text"
            placeholder="10001"
            maxLength={5}
            value={billingForm.postalCode}
            onChange={(e) => setBillingForm({ ...billingForm, postalCode: e.target.value })}
          />
        </div>

        <div className="pay-form-actions pay-form-full">
          <button
            type="submit"
            className="pay-btn-primary"
            disabled={saving || !stripe || !cardComplete}
          >
            {saving ? 'Saving...' : 'Save Card'}
          </button>
          <button type="button" className="pay-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatCents = (cents) => {
  if (!cents && cents !== 0) return '$0';
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  // Mix of bare YYYY-MM-DD (closingDate — safe) and real instants
  // (closedAt/paidAt — need viewer-zone rendering). Pass the viewer zone
  // either way; for bare dates the browser already extracts parts without a
  // zone shift when the Date was built at local midnight.
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    timeZone: getViewerTimezone(),
  });
};

// The "closed" column falls back across a mix of pure calendar dates
// (closingDate/closeDate — no time-of-day, safe to render as-is) and real
// instants (Transaction.closedAt — bare backend LocalDateTime; charge
// paidAt/createdAt — Stripe timestamps). Only the real-instant candidates
// need viewer-timezone conversion; converting a pure date through a zone
// lens risks shifting it onto the wrong day.
//
// updatedAt is the final backstop for transactions closed before 2026-09-11
// (when backend started stamping closedAt). It's not the actual close time,
// but it's within hours of it for a status→CLOSED write and much better than
// a dash. Mobile has used this fallback since launch (PaymentsScreen.tsx:228).
const formatClosedDate = (txn, charge) => {
  if (txn?.closedAt) return formatUtcNaiveInstantForViewer(txn.closedAt).dateLabel || '—';
  if (txn?.closingDate) return formatDate(txn.closingDate);
  if (txn?.closeDate) return formatDate(txn.closeDate);
  if (charge?.paidAt) return formatUtcNaiveInstantForViewer(charge.paidAt).dateLabel || '—';
  if (charge?.createdAt) return formatUtcNaiveInstantForViewer(charge.createdAt).dateLabel || '—';
  if (txn?.updatedAt) return formatUtcNaiveInstantForViewer(txn.updatedAt).dateLabel || '—';
  return '—';
};

const getStatusBadgeClass = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'pay-badge pay-badge-green';
  if (s === 'UNDER_CONTRACT') return 'pay-badge pay-badge-blue';
  if (s === 'CLOSING') return 'pay-badge pay-badge-purple';
  return 'pay-badge pay-badge-grey';
};

// Default fee shown on closed rows that have no PaymentIntent yet.
// Matches rialty-payment-service rialty.charge.default-amount-cents.
const DEFAULT_FEE_CENTS = 10000;

// Map a Stripe charge (PaymentIntent) + the transaction's persisted
// payment status into a single UI state so the row body doesn't need
// to branch over Stripe's raw enum.
//
// Reference: https://stripe.com/docs/payments/paymentintents/lifecycle
// kind:
//   'pay'     → create a new PaymentIntent (handleCreateCharge)
//   'retry'   → continue the existing PI via confirm (handleRetryCharge)
//   'receipt' → payment settled, show receipt link
//   'none'    → in-flight on Stripe's side, no user action
//
// Refund/dispute states are only tracked in tx-service (Stripe PI status
// stays "succeeded" after a refund — the refund lives on the Charge).
// We check txn.paymentStatus FIRST for those three; otherwise fall through
// to the Stripe-PI-status mapping.
const derivePaymentUiState = (charge, txn) => {
  const txStatus = txn?.paymentStatus;
  if (txStatus === 'REFUNDED') {
    return {
      badgeClass: 'pay-badge pay-badge-grey',
      label: 'Refunded',
      actionKind: 'receipt',
      actionLabel: 'View Receipt',
      refundedCents: txn?.refundedAmountCents,
    };
  }
  if (txStatus === 'PARTIALLY_REFUNDED') {
    return {
      badgeClass: 'pay-badge pay-badge-amber',
      label: 'Partially Refunded',
      actionKind: 'receipt',
      actionLabel: 'View Receipt',
      refundedCents: txn?.refundedAmountCents,
    };
  }
  if (txStatus === 'DISPUTED') {
    return {
      badgeClass: 'pay-badge pay-badge-red',
      label: 'Disputed',
      actionKind: 'none',
      actionLabel: null,
    };
  }
  const status = (charge?.status || '').toLowerCase();
  // No PI, or Stripe `canceled` (canceled PIs cannot be reconfirmed —
  // Pay Now has to start a fresh PI).
  if (!charge || status === 'canceled') {
    return {
      badgeClass: 'pay-badge pay-badge-grey',
      label: 'Due',
      actionKind: 'pay',
      actionLabel: 'Pay Now',
    };
  }
  if (status === 'succeeded') {
    return {
      badgeClass: 'pay-badge pay-badge-green',
      label: 'Paid',
      actionKind: 'receipt',
      actionLabel: 'View Receipt',
    };
  }
  if (status === 'processing' || status === 'requires_capture') {
    return {
      badgeClass: 'pay-badge pay-badge-amber',
      label: 'Processing',
      actionKind: 'none',
      actionLabel: null,
    };
  }
  if (status === 'requires_payment_method') {
    return {
      badgeClass: 'pay-badge pay-badge-red',
      label: 'Failed',
      actionKind: 'retry',
      actionLabel: 'Retry',
    };
  }
  if (status === 'requires_action' || status === 'requires_confirmation') {
    return {
      badgeClass: 'pay-badge pay-badge-amber',
      label: 'Action Required',
      actionKind: 'retry',
      actionLabel: 'Complete Payment',
    };
  }
  // Unknown status — show raw and give no action rather than guess.
  return {
    badgeClass: 'pay-badge pay-badge-grey',
    label: status || '—',
    actionKind: 'none',
    actionLabel: null,
  };
};

const getPropertyAddress = (txn) => {
  if (!txn) return '—';
  return (
    txn.propertyAddress ||
    txn.address ||
    txn.property?.address ||
    txn.title ||
    `Transaction #${txn.id || ''}` ||
    '—'
  );
};

const getBrandDisplay = (brand) => {
  const b = (brand || '').toUpperCase();
  if (b === 'VISA') return { text: 'VISA', color: '#1A1F71' };
  if (b === 'MASTERCARD' || b === 'MC') return { text: 'MC', color: '#EB001B' };
  if (b === 'AMEX') return { text: 'AMEX', color: '#007BC1' };
  if (b === 'DISCOVER') return { text: 'DISC', color: '#FF6600' };
  return { text: brand || 'CARD', color: '#64748B' };
};

// ── Component ─────────────────────────────────────────────────────────────────

const PaymentsPage = () => {
  const navigate = useNavigate();
  // Buyer/Seller are read-only participants — closing a transaction is an agent
  // action (the backend rejects it for them), so Mark Closed is hidden for these
  // two roles only. Unknown/missing role keeps the button (previous behavior).
  const isBuyerOrSellerRole = ['BUYER', 'SELLER'].includes(
    (getActiveRoleFromStorage() || '').toUpperCase().replace(/^ROLE_/, '')
  );
  const [summary, setSummary] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [closedTransactions, setClosedTransactions] = useState([]);
  const [charges, setCharges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMarkClosedModal, setShowMarkClosedModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showPayNowModal, setShowPayNowModal] = useState(false);
  const [payNowTransaction, setPayNowTransaction] = useState(null);
  const [isCreatingCharge, setIsCreatingCharge] = useState(false);
  // Stripe Checkout Session client secret for the embedded Pay-Now flow.
  // When set, the Pay-Now modal body is replaced with <EmbeddedCheckout />.
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [toast, setToast] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  // When Pay Now is clicked on a txn but the user has no saved card, we
  // stash the txn here, close the Pay Now modal, open the Add Card form,
  // and auto-reopen the Pay Now modal once the card is attached.
  const [pendingPayAfterCardAdd, setPendingPayAfterCardAdd] = useState(null);
  // Active Transactions section collapsed by default — this list is noisy
  // for high-volume agents and the primary CTA (View) now sends them to
  // the transaction detail page where Mark Closed lives on the timeline.
  const [activeExpanded, setActiveExpanded] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3200);
  };

  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) { auth.currentUser.getIdToken(true).then(resolve).catch(reject); return; }
      const tid = setTimeout(() => { unsub(); reject(new Error('auth timeout')); }, 10000);
      const unsub = auth.onAuthStateChanged(async (u) => {
        clearTimeout(tid); unsub();
        if (u) { try { resolve(await u.getIdToken(true)); } catch (e) { reject(e); } }
        else reject(new Error('No authenticated user'));
      });
    });
  }, []);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [summaryRes, methodsRes, activeRes, closedRes, chargesRes] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.paymentSummary, { headers }),
        apiClient.get(API_ENDPOINTS.paymentMethods, { headers }),
        apiClient.get(API_ENDPOINTS.transactions, { headers, params: { status: 'ACTIVE,UNDER_CONTRACT,CLOSING' } }),
        apiClient.get(API_ENDPOINTS.transactions, { headers, params: { status: 'CLOSED' } }),
        apiClient.get(API_ENDPOINTS.paymentCharges, { headers }),
      ]);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
      if (methodsRes.status === 'fulfilled') {
        const d = methodsRes.value.data;
        setPaymentMethods(Array.isArray(d) ? d : d?.data || d?.content || []);
      }
      if (activeRes.status === 'fulfilled') {
        const d = activeRes.value.data;
        setActiveTransactions(Array.isArray(d) ? d : d?.data || d?.content || []);
      }
      if (closedRes.status === 'fulfilled') {
        const d = closedRes.value.data;
        setClosedTransactions(Array.isArray(d) ? d : d?.data || d?.content || []);
      }
      if (chargesRes.status === 'fulfilled') {
        const d = chargesRes.value.data;
        setCharges(Array.isArray(d) ? d : d?.data || d?.content || []);
      }
    } catch (e) {
      console.error('Failed to load payments:', e);
    } finally {
      setIsLoading(false);
    }
  }, [getFirebaseToken]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSetDefault = async (methodId) => {
    try {
      const token = await getFirebaseToken();
      // Mobile uses PUT — matches backend contract
      await apiClient.put(
        API_ENDPOINTS.setDefaultPaymentMethod(methodId),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Default payment method updated');
      loadAll();
    } catch (e) {
      showToast('Failed to update default method');
    }
  };

  const handleRemoveMethod = async (methodId) => {
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(API_ENDPOINTS.paymentMethodById(methodId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Payment method removed');
      loadAll();
    } catch (e) {
      showToast('Failed to remove payment method');
    }
  };

  // Walk through valid status transitions to reach CLOSED, matching mobile app logic
  const closeTransactionBySteps = async (txn, token) => {
    const currentStatus = (txn.status || '').toUpperCase();
    const steps = [];
    if (currentStatus === 'ACTIVE' || currentStatus === 'PENDING') {
      steps.push('UNDER_CONTRACT', 'CLOSING', 'CLOSED');
    } else if (currentStatus === 'UNDER_CONTRACT') {
      steps.push('CLOSING', 'CLOSED');
    } else if (currentStatus === 'CLOSING') {
      steps.push('CLOSED');
    } else {
      // Already at or past CLOSED — send CLOSED directly as fallback
      steps.push('CLOSED');
    }
    for (const nextStatus of steps) {
      await apiClient.post(
        API_ENDPOINTS.changeTransactionStatus(txn.id),
        { status: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
  };

  const handleMarkClosed = async () => {
    if (!selectedTransaction) return;
    try {
      const token = await getFirebaseToken();
      await closeTransactionBySteps(selectedTransaction, token);
      setShowMarkClosedModal(false);
      showToast('Transaction marked as closed');
      await loadAll();
      // After closing, prompt user to pay now (same as mobile)
      setPayNowTransaction(selectedTransaction);
      setShowPayNowModal(true);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to mark as closed');
    }
  };

  // Create a new charge for a just-closed transaction (web equivalent of mobile's createPaymentSheetIntent)
  // Fetches a Stripe Checkout Session client secret from the backend. The
  // returned secret is passed to <EmbeddedCheckoutProvider>, which mounts
  // Stripe's hosted iframe inside this page's Pay-Now modal. Payment
  // confirmation + status mirroring happens via Stripe webhook, not here.
  const handleCreateCharge = async () => {
    if (!payNowTransaction) return;
    setIsCreatingCharge(true);
    setCheckoutError(null);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.post(
        API_ENDPOINTS.paymentCheckoutSession,
        {
          transactionId: payNowTransaction.id,
          transactionName: getPropertyAddress(payNowTransaction),
          returnUrl: `${window.location.origin}/payments?checkout_session={CHECKOUT_SESSION_ID}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const secret = res?.data?.data?.clientSecret;
      if (!secret) {
        throw new Error('Checkout session response missing clientSecret');
      }
      setCheckoutClientSecret(secret);
    } catch (e) {
      setCheckoutError(e?.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setIsCreatingCharge(false);
    }
  };

  // Called by <EmbeddedCheckout onComplete> when Stripe tells the client the
  // user finished the flow. We clear local state + refresh — the payment's
  // READY status lands via the webhook, not synchronously here.
  const handleCheckoutComplete = () => {
    setCheckoutClientSecret(null);
    setShowPayNowModal(false);
    setPayNowTransaction(null);
    showToast('Payment submitted. Status will update shortly.');
    loadAll();
  };

  // User closes the modal mid-checkout. Dispose the client secret so the
  // next open starts a fresh session.
  const closePayNowModal = () => {
    setCheckoutClientSecret(null);
    setCheckoutError(null);
    setShowPayNowModal(false);
    setPayNowTransaction(null);
  };

  const handleRetryCharge = async () => {
    if (!selectedCharge) return;
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.retryPaymentCharge(selectedCharge.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowRetryModal(false);
      showToast('Charge retried successfully');
      loadAll();
    } catch (e) {
      showToast('Retry failed. Please try again.');
    }
  };


  // ── Computed totals ──────────────────────────────────────────────────────────

  const totalCollectedCents = charges
    .filter((c) => (c.status || '').toLowerCase() === 'succeeded')
    .reduce((sum, c) => sum + (c.amount || c.amountCents || 0), 0);

  const outstandingCents = charges
    .filter((c) => {
      const s = (c.status || '').toLowerCase();
      return s === 'pending' || s === 'failed';
    })
    .reduce((sum, c) => sum + (c.amount || c.amountCents || 0), 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="pay-page">
      <div className="pay-main-content">
      <div className="pay-content-box">
      <div className="pay-content">
        {/* Page Header */}
        <div className="pay-page-header">
          <div className="pay-page-header-left">
            <button className="pay-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
            <h1 className="pay-page-title">Payments &amp; Payouts</h1>
            <p className="pay-page-subtitle">
              Manage your transaction billing&nbsp;&middot;&nbsp;Powered by Stripe
            </p>
          </div>
          <button
            className="pay-btn-primary"
            onClick={() => setShowAddCard(!showAddCard)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Payment Method
          </button>
        </div>

        {isLoading ? (
          <div className="pay-loading-state">
            <div className="pay-spinner" />
            <span>Loading payment data...</span>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="pay-summary-row">
              <div className="pay-summary-card pay-summary-blue">
                <div className="pay-summary-icon-wrap pay-summary-icon-blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                  </svg>
                </div>
                <div className="pay-summary-body">
                  <div className="pay-summary-label">Active Transactions</div>
                  <div className="pay-summary-value">{summary?.activeCount ?? activeTransactions.length}</div>
                  <div className="pay-summary-sub">
                    {summary?.nextCloseDate
                      ? `Next close: ${formatDate(summary.nextCloseDate)}`
                      : 'Active'}
                  </div>
                </div>
              </div>

              <div className="pay-summary-card pay-summary-green">
                <div className="pay-summary-icon-wrap pay-summary-icon-green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div className="pay-summary-body">
                  <div className="pay-summary-label">Payments Received</div>
                  <div className="pay-summary-value">
                    {formatCents(summary?.receivedTotalCents ?? totalCollectedCents)}
                  </div>
                  <div className="pay-summary-sub">
                    {summary?.receivedCount ?? charges.filter(c => (c.status || '').toLowerCase() === 'succeeded').length} transactions
                  </div>
                </div>
              </div>

              <div className="pay-summary-card pay-summary-amber">
                <div className="pay-summary-icon-wrap pay-summary-icon-amber">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div className="pay-summary-body">
                  <div className="pay-summary-label">Payments Pending</div>
                  <div className="pay-summary-value">
                    {formatCents(summary?.pendingTotalCents ?? outstandingCents)}
                  </div>
                  <div className="pay-summary-sub">
                    {summary?.pendingCount ?? charges.filter(c => { const s = (c.status || '').toLowerCase(); return s === 'pending' || s === 'failed'; }).length} pending
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <section className="pay-section">
              <div className="pay-section-header">
                <h2 className="pay-section-title">Payment Methods</h2>
              </div>

              {/* Add Card Form — Stripe Elements */}
              {showAddCard && (
                <Elements stripe={stripePromise}>
                  <AddCardFormInner
                    getFirebaseToken={getFirebaseToken}
                    showToast={showToast}
                    onSuccess={async () => {
                      setShowAddCard(false);
                      await loadAll();
                      // If the user got here from a Pay Now click on a
                      // cardless account, reopen the Process Payment modal
                      // for the stashed txn.
                      if (pendingPayAfterCardAdd) {
                        setPayNowTransaction(pendingPayAfterCardAdd);
                        setPendingPayAfterCardAdd(null);
                        setShowPayNowModal(true);
                      }
                    }}
                    onCancel={() => {
                      setShowAddCard(false);
                      setPendingPayAfterCardAdd(null);
                    }}
                  />
                </Elements>
              )}

              <div className="pay-methods-grid">
                {paymentMethods.length === 0 && !showAddCard ? (
                  <div className="pay-empty-state">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <p>No payment methods on file.</p>
                    <button className="pay-btn-primary" onClick={() => setShowAddCard(true)}>Add your first card</button>
                  </div>
                ) : (
                  <>
                    {paymentMethods.map((method) => {
                      const brand = getBrandDisplay(method.brand || method.cardBrand);
                      const last4 = method.last4 || method.lastFour || '????';
                      const expMonth = String(method.expMonth || method.expiryMonth || '??').padStart(2, '0');
                      const expYear = String(method.expYear || method.expiryYear || '??').slice(-2);
                      return (
                        <div key={method.id} className={`pay-method-card${method.isDefault ? ' pay-method-card-default' : ''}`}>
                          <div className="pay-method-top">
                            <div className="pay-method-brand" style={{ color: brand.color }}>
                              {brand.text}
                            </div>
                            {method.isDefault && (
                              <span className="pay-default-badge">Default</span>
                            )}
                          </div>
                          <div className="pay-method-number">
                            &bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;&nbsp;{last4}
                          </div>
                          <div className="pay-method-expiry">
                            Expires {expMonth} / {expYear}
                          </div>
                          <div className="pay-method-actions">
                            {!method.isDefault && (
                              <button
                                className="pay-btn-sm pay-btn-outline"
                                onClick={() => handleSetDefault(method.id)}
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              className="pay-btn-sm pay-btn-danger-ghost"
                              onClick={() => handleRemoveMethod(method.id)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add New Card tile */}
                    <button
                      className="pay-method-add-tile"
                      onClick={() => setShowAddCard(true)}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add New Card
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* Active Transactions Table — collapsible */}
            <section className="pay-section">
              <div
                className="pay-section-header"
                onClick={() => setActiveExpanded((v) => !v)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <h2 className="pay-section-title">Active Transactions</h2>
                <span className="pay-section-count">{activeTransactions.length}</span>
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 'auto', fontSize: '14px', color: '#64748B',
                    transform: activeExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease',
                  }}
                >▶</span>
              </div>

              {activeExpanded && (
                activeTransactions.length === 0 ? (
                  <div className="pay-table-empty">No active transactions found.</div>
                ) : (
                  <div className="pay-table-wrap">
                    <table className="pay-table">
                      <thead>
                        <tr>
                          <th>Property Address</th>
                          <th>Status</th>
                          <th>Est. Close Date</th>
                          <th>Fee Due</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeTransactions.map((txn) => (
                          <tr key={txn.id}>
                            <td className="pay-td-address">{getPropertyAddress(txn)}</td>
                            <td>
                              <span className={getStatusBadgeClass(txn.status)}>
                                {(txn.status || '').replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td>{formatDate(txn.estimatedCloseDate || txn.closeDate || txn.closingDate)}</td>
                            <td className="pay-td-fee">On close</td>
                            <td>
                              <button
                                className="pay-btn-sm pay-btn-outline"
                                onClick={() => navigate(`/transaction/${txn.id}`, { state: { returnTo: '/profile' } })}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </section>

            {/* Closed Transactions / Charges Table */}
            <section className="pay-section">
              <div className="pay-section-header">
                <h2 className="pay-section-title">Closed Transactions</h2>
                <span className="pay-section-count">{closedTransactions.length}</span>
              </div>

              {closedTransactions.length === 0 ? (
                <div className="pay-table-empty">No closed transactions found.</div>
              ) : (
                <div className="pay-table-wrap">
                  <table className="pay-table">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Closed On</th>
                        <th>Amount</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closedTransactions.map((txn) => {
                        // Join with charges — find the charge for this transaction
                        const charge = charges.find(
                          (c) => c.transactionId === txn.id || c.transactionId === txn.transactionId
                        );
                        const ui = derivePaymentUiState(charge, txn);
                        // Default fee shown when no PI exists yet; a future
                        // txn.chargeable=false can override to $0.
                        const amountCents = charge
                          ? (charge.amount || charge.amountCents)
                          : (txn.chargeable === false ? 0 : DEFAULT_FEE_CENTS);
                        const refundedCents = ui.refundedCents;
                        return (
                          <tr key={txn.id}>
                            <td className="pay-td-address">{getPropertyAddress(txn)}</td>
                            <td>{formatClosedDate(txn, charge)}</td>
                            <td className="pay-td-amount">
                              {formatCents(amountCents)}
                              {refundedCents != null && (
                                <div className="pay-td-muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                                  Refunded {formatCents(refundedCents)}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={ui.badgeClass}>{ui.label}</span>
                            </td>
                            <td>
                              {ui.actionKind === 'pay' && (
                                <button
                                  className="pay-btn-sm pay-btn-primary-sm"
                                  onClick={() => { setPayNowTransaction(txn); setShowPayNowModal(true); }}
                                >
                                  {ui.actionLabel}
                                </button>
                              )}
                              {ui.actionKind === 'retry' && charge && (
                                <button
                                  className="pay-btn-sm pay-btn-primary-sm"
                                  onClick={() => { setSelectedCharge(charge); setShowRetryModal(true); }}
                                >
                                  {ui.actionLabel}
                                </button>
                              )}
                              {ui.actionKind === 'receipt' && charge?.receiptUrl && (
                                <a
                                  href={charge.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="pay-btn-sm pay-btn-outline"
                                >
                                  {ui.actionLabel}
                                </a>
                              )}
                              {ui.actionKind === 'receipt' && !charge?.receiptUrl && (
                                <span className="pay-td-muted">Paid</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Total Cards */}
            <div className="pay-summary-row">
              <div className="pay-summary-card pay-summary-green">
                <div className="pay-summary-icon-wrap pay-summary-icon-green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div className="pay-summary-body">
                  <div className="pay-summary-label">Total Collected</div>
                  <div className="pay-summary-value pay-total-green">
                    {formatCents(summary?.receivedTotalCents ?? totalCollectedCents)}
                  </div>
                  <div className="pay-summary-sub">All-time via Stripe</div>
                </div>
              </div>

              <div className="pay-summary-card pay-summary-amber">
                <div className="pay-summary-icon-wrap pay-summary-icon-amber">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div className="pay-summary-body">
                  <div className="pay-summary-label">Outstanding</div>
                  <div className="pay-summary-value pay-total-amber">
                    {formatCents(summary?.pendingTotalCents ?? outstandingCents)}
                  </div>
                  <div className="pay-summary-sub">Pending / failed charges</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      </div>
      </div>

      {/* Mark Closed Modal */}
      {showMarkClosedModal && selectedTransaction && (
        <div className="pay-modal-overlay" onClick={() => setShowMarkClosedModal(false)}>
          <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pay-modal-header">
              <h3 className="pay-modal-title">Mark Transaction as Closed</h3>
              <button className="pay-modal-close" onClick={() => setShowMarkClosedModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="pay-modal-body">
              <p>Are you sure you want to mark <strong>{getPropertyAddress(selectedTransaction)}</strong> as closed?</p>
              <p className="pay-modal-note">
                This action will update the transaction status to CLOSED. A{' '}
                <strong>{formatCents(DEFAULT_FEE_CENTS)} closure fee</strong>{' '}
                will be charged to your default payment method. You can also pay later from the Closed Transactions section.
              </p>
            </div>
            <div className="pay-modal-footer">
              <button className="pay-btn-ghost" onClick={() => setShowMarkClosedModal(false)}>Cancel</button>
              <button className="pay-btn-primary" onClick={handleMarkClosed}>Confirm &amp; Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Now Modal — for newly-closed transactions with no existing charge.
          Three internal states driven by local flags:
            1. Confirmation prompt (default) — asks the user to proceed
            2. Loading (isCreatingCharge true) — fetching clientSecret
            3. EmbeddedCheckout (checkoutClientSecret set) — Stripe-hosted
               iframe inside this modal with card entry + dynamic payment
               methods (Apple Pay, Link, etc.) auto-configured */}
      {showPayNowModal && payNowTransaction && (
        <div className="pay-modal-overlay" onClick={closePayNowModal}>
          <div
            className={`pay-modal ${checkoutClientSecret ? 'pay-modal-checkout' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pay-modal-header">
              <h3 className="pay-modal-title">
                {checkoutClientSecret ? 'Complete payment' : 'Process Payment'}
              </h3>
              <button className="pay-modal-close" onClick={closePayNowModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {checkoutClientSecret ? (
              <div className="pay-modal-checkout-body">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret: checkoutClientSecret,
                    onComplete: handleCheckoutComplete,
                  }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <>
                <div className="pay-modal-body">
                  <p>
                    Would you like to process payment for{' '}
                    <strong>{getPropertyAddress(payNowTransaction)}</strong>?
                  </p>
                  <p className="pay-modal-note">
                    You'll choose or enter a payment method on the next step.
                  </p>
                  {checkoutError && (
                    <p className="pay-modal-error" style={{ color: '#C62828', marginTop: 8 }}>
                      {checkoutError}
                    </p>
                  )}
                </div>
                <div className="pay-modal-footer">
                  <button className="pay-btn-ghost" onClick={closePayNowModal}>
                    Pay Later
                  </button>
                  <button className="pay-btn-primary" onClick={handleCreateCharge} disabled={isCreatingCharge}>
                    {isCreatingCharge ? 'Starting checkout…' : 'Pay Now'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Retry / Pay Now Modal */}
      {showRetryModal && selectedCharge && (
        <div className="pay-modal-overlay" onClick={() => setShowRetryModal(false)}>
          <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pay-modal-header">
              <h3 className="pay-modal-title">Retry Payment</h3>
              <button className="pay-modal-close" onClick={() => setShowRetryModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="pay-modal-body">
              <p>
                Retry the charge of <strong>{formatCents(selectedCharge.amount || selectedCharge.amountCents)}</strong> for{' '}
                <strong>{selectedCharge.propertyAddress || selectedCharge.description || `Charge #${selectedCharge.id}`}</strong>?
              </p>
              <p className="pay-modal-note">The charge will be attempted on your default payment method.</p>
            </div>
            <div className="pay-modal-footer">
              <button className="pay-btn-ghost" onClick={() => setShowRetryModal(false)}>Cancel</button>
              <button className="pay-btn-primary" onClick={handleRetryCharge}>Pay Now</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="pay-toast">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
